<template>
  <v-layout>
    <v-flex>
      <v-layout align-center>
        <h1 class="headline">Departments</h1>
        <v-layout wrap>
          <v-spacer></v-spacer>
          <v-flex xs12 sm4 class="pb-2">
            <v-text-field
              append-icon="mdi-magnify"
              label="Quick Filter..."
              single-line
              hide-details
              v-model="search"
              class="ma-0"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                dark
                color="primary"
                @click="createItem"
                class="mx-2"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Create New Department</span>
          </v-tooltip>
        </v-layout>
      </v-layout>
      <v-card>
        <v-data-table
          :headers="headers"
          :items="departments.edges"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="node.id"
        >
          <template v-slot:item.notInUse="{ item }">
            <v-checkbox
              v-model="item.node.notInUse"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hide-view="true"
              @deleteItem="deleteItem($event)"
              @editItem="editItem($event)"
            >
              <template v-slot:extraButton>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="mx-0"
                      @click="editItemContents(item)"
                    >
                      <v-icon color="blue" small>mdi-camera</v-icon>
                    </v-btn>
                  </template>
                  <span>Department Contents</span>
                </v-tooltip>
              </template>
            </default-list-actions>
          </template>
        </v-data-table>
      </v-card>
      <template v-if="selectedDepartment && selectedDepartment.id">
        <v-dialog
          v-model="departmentContentsDialog"
          min-width="1200"
          persistent
          :key="selectedDepartment && selectedDepartment.id"
          scrollable
        >
          <v-card>
            <v-card-title class="grey lighten-3">
              <v-row>
                <v-icon class="draggable">mdi-drag</v-icon>
                <v-col class="py-0"
                  >{{ selectedDepartment.name }} - Contents</v-col
                >
              </v-row>
            </v-card-title>
            <v-card-text>
              <contents
                v-if="selectedDepartment.id != null"
                :owner-id="selectedDepartment.id"
                owner-name="DEPARTMENT"
              ></contents>
            </v-card-text>
            <v-card-actions class="grey lighten-4">
              <v-spacer></v-spacer>
              <v-btn
                color="warning"
                text
                @click="departmentContentsDialog = false"
                >Close</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <department-edit-dialog
        v-model="selectedDepartment"
        :show="departmentEditorDialog"
        @close="departmentEditorDialog = false"
        @input="onSaveDepartment"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import {
  ALL_DEPARTMENTS_SEARCH_QUERY,
  DEPARTMENT_DELETE_MUTATION,
  DEPARTMENT_CREATE_MUTATION,
  DEPARTMENT_UPDATE_MUTATION,
} from "../../../../constants/graphql";
import DepartmentEditor from "./DepartmentEditor";
import DefaultListActions from "../../shared/DefaultListActions";
import Contents from "../../shared/contents/Contents";
import { pagingOptions, removeGQLTypename } from "../../../../helpers/index";
import DepartmentEditDialog from "./DepartmentEditDialog.vue";

export default {
  name: "DepartmentsList",

  components: {
    DefaultListActions,
    DepartmentEditor,
    Contents,
    DepartmentEditDialog,
  },

  data() {
    return {
      pagingOptions,
      search: "",
      selectedDepartment: {},
      doSaveDepartment: false,
      departments: {},
      departmentEditorDialog: false,
      departmentContentsDialog: false,
      headers: [
        {
          text: "Name",
          value: "node.name",
          name: "name",
        },
        {
          text: "Tax Code",
          value: "node.taxType",
          name: "taxType",
        },
        {
          text: "Sales Nominal Code",
          value: "node.salesNominalCode",
          name: "salesNominalCode",
        },
        {
          text: "Purchase Nominal Code",
          value: "node.purchaseNominalCode",
          name: "purchaseNominalCode",
        },
        {
          text: "Not In Use",
          value: "notInUse",
          name: "notInUse",
        },
        {
          text: "Code",
          value: "node.code",
          name: "code",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },

  methods: {
    createItem() {
      this.selectedDepartment = {};
      this.departmentEditorDialog = true;
    },
    editItem(item) {
      //console.log("editItem", item.node);
      this.selectedDepartment = {
        ...item.node,
        paymentTerms: item.node.paymentTerms.edges.map(removeGQLTypename),
      };
      this.departmentEditorDialog = true;
    },
    editItemContents(item) {
      this.selectedDepartment = item.node;
      this.departmentContentsDialog = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DEPARTMENT_DELETE_MUTATION,
          variables: {
            departmentId: item.node.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Department removed." });

      if (this.$apollo.queries.departments != null) {
        this.$apollo.queries.departments.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.departments != null) {
        this.$apollo.queries.departments.refetch();
      }
      this.onResetSave();
      this.departmentEditorDialog = false;
      this.selectedDepartment = null;
      let msg =
        context == "edit" ? "Department updated." : "Department created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    onResetSave() {
      this.doSaveDepartment = false;
    },
    closeDialog() {
      this.departmentEditorDialog = false;
      this.selectedDepartment = null;
    },
    onSaveDepartment(payload) {
      this.selectedDepartment = null;
      const { id, ...input } = payload;
      const mutation = id
        ? DEPARTMENT_UPDATE_MUTATION
        : DEPARTMENT_CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation,
          variables: { id, input },
          refetchQueries: [
            {
              query: ALL_DEPARTMENTS_SEARCH_QUERY,
            },
          ],
        })
        .then(() => {
          this.departmentEditorDialog = false;
          this.$store.dispatch("showAppSnackbar", {
            msg: "Department Saved.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    departments: {
      query: ALL_DEPARTMENTS_SEARCH_QUERY,
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/admin/department-types/DepartmentsList.vue